import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { emitDebugEvent } from '@/features/copilot-debug';
import { analytics, logger } from '@/shared/lib';
import { expandCopilotPanel, setCopilotActiveTab, closeCopilotOverlay } from '@/shared/stores/ui';

import {
  CopilotSessionContext,
  type CopilotSessionValue,
  type SendCopilotTextFn,
} from './CopilotSessionContext';
import { useCopilotChat } from './useCopilotChat';
import { useCopilotThreads } from './useCopilotThreads';

const THREAD_AUTOSAVE_DELAY_MS = 2000;

export interface CopilotSessionProviderProps {
  userId: string;
  children: ReactNode;
}

export function CopilotSessionProvider({ userId, children }: CopilotSessionProviderProps) {
  const threads = useCopilotThreads({ userId });
  const chat = useCopilotChat({ userId, threadId: threads.activeThreadId });

  const { messages, status, sendMessage } = chat;
  const isBusy = status === 'submitted' || status === 'streaming';
  const activeThreadId = threads.activeThreadId;

  const saveThreadRef = useRef(threads.saveThread);
  useEffect(() => {
    saveThreadRef.current = threads.saveThread;
  }, [threads.saveThread]);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (messages.length === 0 || isBusy) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void saveThreadRef.current(activeThreadId, messages);
    }, THREAD_AUTOSAVE_DELAY_MS);
    return () => clearTimeout(saveTimerRef.current);
  }, [messages, isBusy, activeThreadId]);

  const sendText: SendCopilotTextFn = useCallback(
    ({ text, source }) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;

      logger.info('copilot.chat.userMessage.send', {
        userId,
        textLength: trimmed.length,
        source,
      });
      void analytics.track({
        name: 'copilot.chat.userMessage.send',
        context: { actor: 'clinician', patientUserId: userId },
        properties: { userId, textLength: trimmed.length, source },
      });
      emitDebugEvent('ai.user.messageSent', 'User message sent', {
        userId,
        textLength: trimmed.length,
        source,
      });

      expandCopilotPanel();
      setCopilotActiveTab('copilot');
      closeCopilotOverlay('followUps');

      void sendMessage({ text: trimmed });
    },
    [sendMessage, userId]
  );

  const value = useMemo<CopilotSessionValue>(
    () => ({ userId, chat, threads, sendText }),
    [userId, chat, threads, sendText]
  );

  return <CopilotSessionContext.Provider value={value}>{children}</CopilotSessionContext.Provider>;
}
